import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RAW_BASE = (
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE ||
  ''
).trim();

const BASE_NO_SLASH = RAW_BASE.replace(/\/+$/, '');

const API = BASE_NO_SLASH
  ? BASE_NO_SLASH.endsWith('/api')
    ? BASE_NO_SLASH
    : `${BASE_NO_SLASH}/api`
  : '/api';

async function request(url, { method = 'GET', body, headers } = {}) {
  const options = {
    method,
    headers: {
      ...(headers || {}),
    },
  };

  if (body !== undefined) {
    options.headers['Content-Type'] = 'application/json';
    options.body = JSON.stringify(body);
  }

  const response = await fetch(url, options);
  const text = await response.text();

  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch (_) {
    data = text;
  }

  if (!response.ok) {
    const detail =
      data?.detail ||
      data?.non_field_errors ||
      response.statusText ||
      'Request failed';

    const error = new Error(
      typeof detail === 'string'
        ? detail
        : JSON.stringify(detail)
    );

    error.response = {
      status: response.status,
      data,
    };

    throw error;
  }

  return data;
}

function Step({ number, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-indigo-50 text-sm font-semibold text-indigo-700 ring-1 ring-inset ring-indigo-100">
        {number}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900">
          {title}
        </h3>

        <p className="mt-1 text-sm leading-6 text-gray-600">
          {children}
        </p>
      </div>
    </div>
  );
}

export default function SubstitutionCounselLandingPage() {
  const navigate = useNavigate();

  const [caseNumber, setCaseNumber] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');


  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

  const cleanedCaseNumber = caseNumber.trim();
const caseNumberPattern = /^\d{2}-\d{5}$/;

if (!cleanedCaseNumber) {
  setError('Please enter your bankruptcy case number.');
  return;
}

if (!caseNumberPattern.test(cleanedCaseNumber)) {
  setError('Please enter the case number in YY-12345 format.');
  return;
}  

    setSubmitting(true);

    try {
      const data = await request(
        `${API}/substitution-counsel/verify/`,
        {
          method: 'POST',
          body: {
            case_number: cleanedCaseNumber,
          },
        }
      );

      if (!data?.verified) {
        throw new Error(
          data?.detail ||
          'We could not verify the information provided.'
        );
      }

      if (data.redirect_url) {
        navigate(data.redirect_url);
        return;
      }

      if (data.public_token) {
        navigate(
          `/substitution-counsel/case/${data.public_token}`
        );
        return;
      }

      throw new Error(
        'Your case was verified, but the case page could not be opened.'
      );
    } catch (err) {
      setError(
        err?.message ||
        'We could not verify the information provided. Please check your entries and try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              Cisowski Law
            </div>

            <div className="text-xs text-gray-500">
              "The Financial Peace of Mind Attorney"
            </div>
          </div>

          <div className="hidden text-right sm:block">
            <div className="text-sm font-medium text-gray-900">
              Chapter 13 Assistance
            </div>

            <div className="text-xs text-gray-500">
              Request a consultation with our office
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="border-b border-gray-200 bg-white">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50 ring-1 ring-inset ring-indigo-100">
                <svg
                  className="h-7 w-7 text-indigo-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                  />
                </svg>
              </div>

              <p className="mt-6 text-sm font-semibold uppercase tracking-wide text-indigo-600">
                Chapter 13 Case Assistance
              </p>

              <h1 className="mt-3 text-3xl font-semibold tracking-tight text-gray-900 sm:text-4xl">
                Request a consultation about replacement counsel
              </h1>

              <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-gray-600">
                You may need to speak with another attorney about
                continued representation in your Chapter 13
                bankruptcy case. Verify your case below to request a
                consultation with Cisowski Law.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-5xl gap-8 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <div className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-gray-200 sm:p-8">
                <h2 className="text-xl font-semibold text-gray-900">
                  What happens next?
                </h2>

                <div className="mt-7 space-y-7">
                  <Step number="1" title="Verify your case">
                    Enter the case number in the format YY-12345
                  </Step>

                  <Step number="2" title="Review the case">
                    After verification, you will be directed to a
                    page containing basic information about the
                    identified Chapter 13 case.
                  </Step>

                  <Step number="3" title="Request a consultation">
                    Submit your phone number and email address so our
                    office can contact you about possible
                    representation.
                  </Step>
                </div>

                <div className="mt-8 rounded-lg bg-amber-50 p-4 ring-1 ring-inset ring-amber-200">
                  <div className="flex gap-3">
                    <svg
                      className="mt-0.5 h-5 w-5 flex-none text-amber-700"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m9-1.875c0 5.592-4.03 10.235-9.334 11.19a.9.9 0 0 1-.332 0C6.03 21.11 2 16.467 2 10.875V5.989c0-.836.51-1.587 1.287-1.899l8.25-3.3a1.25 1.25 0 0 1 .926 0l8.25 3.3A2.043 2.043 0 0 1 22 5.989v4.886Z"
                      />
                    </svg>

                    <p className="text-sm leading-6 text-amber-900">
                      Submitting this form does not create an
                      attorney-client relationship. Representation
                      begins only after both you and the attorney sign
                      a written agreement.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <div className="rounded-xl bg-white p-6 shadow-lg ring-1 ring-gray-200 sm:p-8">
                <p className="text-sm font-semibold text-indigo-600">
                  Case verification
                </p>

                <h2 className="mt-2 text-2xl font-semibold text-gray-900">
                  Find your Chapter 13 case
                </h2>

                <p className="mt-3 text-sm leading-6 text-gray-600">
                  Enter the information as it appears on your
                  bankruptcy court documents.
                </p>

                <form
                  onSubmit={handleSubmit}
                  className="mt-8 space-y-6"
                >
                  <div>
                    <label
                      htmlFor="case-number"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Bankruptcy case number
                    </label>

<input
  id="case-number"
  name="case_number"
  type="text"
  inputMode="numeric"
  autoComplete="off"
  maxLength={8}
  value={caseNumber}
  onChange={(event) => {
    let value = event.target.value
      .replace(/\D/g, '')
      .slice(0, 7);

    if (value.length > 2) {
      value = `${value.slice(0, 2)}-${value.slice(2)}`;
    }

    setCaseNumber(value);
  }}
  placeholder="Example: 26-12345"
  disabled={submitting}
  className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
/>

<p className="mt-2 text-xs leading-5 text-gray-500">

  Use the two-digit filing year, a dash, and the

  five-digit case number. Example: 26-12345.

</p>
                  </div>




                  {error && (
                    <div
                      role="alert"
                      className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-inset ring-red-200"
                    >
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400"
                  >
                    {submitting ? (
                      <>
                        <svg
                          className="-ml-1 mr-2 h-5 w-5 animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          aria-hidden="true"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />

                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z"
                          />
                        </svg>

                        Verifying case...
                      </>
                    ) : (
                      <>
                        Continue to my case

                        <svg
                          className="ml-2 h-4 w-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.75"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m9 18 6-6-6-6"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-6 border-t border-gray-200 pt-6">
                  <div className="flex gap-3">
                    <svg
                      className="mt-0.5 h-5 w-5 flex-none text-gray-400"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.5 10.5V6.75a4.5 4.5 0 0 0-9 0v3.75m-.75 11.25h10.5A2.25 2.25 0 0 0 19.5 19.5v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                      />
                    </svg>

                    <p className="text-xs leading-5 text-gray-500">
                      The information entered here is used only to
                      verify the case and process your consultation
                      request.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 text-center sm:px-6 lg:px-8">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} Cisowski Law. All rights
            reserved.
          </p>

          <p className="mx-auto mt-3 max-w-3xl text-xs leading-5 text-gray-500">
            This website provides general information and a way to
            request a consultation. It does not provide legal advice
            and does not create an attorney-client relationship.
          </p>
        </div>
      </footer>
    </div>
  );
}